import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Space,
  Table,
  Tag,
  Alert,
  Modal,
  AutoComplete,
  Radio,
  Tooltip,
  message
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useList, useCustom } from "@refinedev/core";
import { TEAM_LIST_QUERY } from "../../teams/queries";
import { PLAN_TEMPLATE_LIST_QUERY } from "../plan-template-queries";
import { MY_ORGANIZATION_QUERY } from "../../organizations/queries";
import { Organization } from "../../organizations/interface";
import { SubmitButton } from "./SubmitButton";
import { IProcurementItem, IProcurementPlan, ITeam } from "../interface";
import generalCategoriesData from "../data/categories.json";
import clinicalCategoriesData from "../data/categories-clinical.json";

type CategoryTypeKey = keyof typeof generalCategoriesData;

const CATEGORY_TYPE_OPTIONS: { value: CategoryTypeKey; label: string; description: string }[] = [
  {
    value: 'GOODS',
    label: 'Goods',
    description: 'Physical, tangible items (e.g., office supplies, laptops)',
  },
  {
    value: 'SERVICES',
    label: 'Services',
    description: 'Intangible activities or expertise (e.g., consulting, software maintenance)',
  },
  {
    value: 'WORKS',
    label: 'Works',
    description: 'Construction, engineering, and infrastructure projects (e.g., building a bridge, road repairs)',
  },
];

// Priority badges for items
const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const priorityColors: Record<string, string> = {
    LOW: 'default',
    MEDIUM: 'processing',
    HIGH: 'warning',
    URGENT: 'error'
  };

  return (
    <Tag color={priorityColors[priority] || 'default'}>
      {priority}
    </Tag>
  );
};

interface ProcurementPlanFormProps {
  form: any;
  isEdit?: boolean;
  initialValues?: Partial<IProcurementPlan>;
  planId?: string;
  onSaveDraft: (values: any) => void;
  onSubmitForApproval?: (values: any) => void;
  onCancel?: () => void;
  saveButtonProps?: any;
  items: IProcurementItem[];
  setItems: (items: IProcurementItem[]) => void;
}

export const ProcurementPlanForm: React.FC<ProcurementPlanFormProps> = ({
  form,
  isEdit = false,
  initialValues = {},
  planId,
  onSaveDraft,
  onSubmitForApproval,
  onCancel,
  saveButtonProps,
  items,
  setItems
}) => {
  const [itemForm] = Form.useForm();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [formErrors] = useState<string[]>([]);
  
  // Track if title/description have been manually edited
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [descriptionManuallyEdited, setDescriptionManuallyEdited] = useState(false);
  
  // Track if default template has been applied to prevent multiple applications
  const defaultTemplateAppliedRef = useRef(false);
  
  // Watch for changes in form fields
  const formValues = Form.useWatch([], { form, preserve: true });
  const teamId = Form.useWatch('teamId', { form });
  const fiscalYear = Form.useWatch('fiscalYear', { form });
  const planningPeriod = Form.useWatch('planningPeriod', { form });
  const templateId = Form.useWatch('templateId', { form });
  const categoryType = Form.useWatch('categoryType', { form: itemForm }) as CategoryTypeKey | undefined;
  
  // Fetch teams using Refine's useList hook
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<ITeam>({
    resource: "teams",
    meta: {
      gqlQuery: TEAM_LIST_QUERY,
    },
  });
  
  // Extract teams from the GraphQL response structure
  // Handle the nested structure from GraphQL: data.teams.nodes
  const teams = (teamsData?.data as any)?.teams?.nodes || teamsData?.data || [];
  
  // Check if requirementDate should be required based on team settings
  const isRequirementDateRequired = React.useMemo(() => {
    if (!teamId || !teams.length) return true; // Default to required if no team selected
    
    const selectedTeam = teams.find((team: ITeam) => team.id === teamId);
    if (!selectedTeam?.settings) return true; // Default to required if no settings
    
    // If OptionalRequirementDateOnPlan is explicitly set to true, make it optional
    // Otherwise (false, undefined, or missing), keep it required
    return !selectedTeam.settings.OptionalRequirementDateOnPlan;
  }, [teamId, teams]);

  // Plan Templates
  const { data: templatesData, isLoading: templatesLoading } = useList<any>({
    resource: "purchaseTemplates",
    meta: { gqlQuery: PLAN_TEMPLATE_LIST_QUERY },
  });
  const templates = ((templatesData?.data && !Array.isArray(templatesData.data)) ? (templatesData.data as any).purchaseTemplates?.nodes || [] : templatesData?.data || [])
    .filter((t: any) => t.templateKind === 'PLAN');

  // Fetch organization to determine industry for category selection
  const { data: orgData } = useCustom<{ myOrganization: Organization | null }>({
    url: "",
    method: "get",
    meta: { gqlQuery: MY_ORGANIZATION_QUERY },
  });
  const industry = orgData?.data?.myOrganization?.serviceIndustry?.toLowerCase();
  const categoriesData = industry?.includes('clinical')
    ? clinicalCategoriesData
    : generalCategoriesData;

  useEffect(() => {
    if (isEdit && initialValues) {
      if (initialValues.templateId) {
        form.setFieldsValue({ templateId: initialValues.templateId });
      }
    }
  }, [isEdit, initialValues, form]);

  // Auto-select first available plan template for new plans
  useEffect(() => {
    if (!isEdit && templates.length > 0 && !form.getFieldValue('templateId')) {
      const firstTemplate = templates[0];
      form.setFieldsValue({ templateId: firstTemplate.id });
      // Apply template settings
      form.setFieldValue("teamId", firstTemplate.teamId);
      // Only set fiscal year if provided in template, otherwise keep default
      if (firstTemplate.fiscalYear) {
        form.setFieldValue("fiscalYear", firstTemplate.fiscalYear);
      }
      form.setFieldValue("steps", firstTemplate.steps);
      form.setFieldValue("approvalType", firstTemplate.approvalType);
      form.setFieldValue("externalWorkflow", firstTemplate.externalWorkflow);
      // Only show message once per component mount
      if (!defaultTemplateAppliedRef.current) {
        defaultTemplateAppliedRef.current = true;
        message.success(`Template '${firstTemplate.name}' applied by default.`);
      }
    }
  }, [isEdit, templates, form]);

  // Auto-generate title and description based on team, fiscal year, and planning period
  useEffect(() => {
    if (teamId && fiscalYear && planningPeriod) {
      const selectedTeam = teams.find((team: ITeam) => team.id === teamId);
      const teamName = selectedTeam?.name || 'Team';
      
      const periodMap: Record<string, string> = {
        'ANNUAL': 'Annual',
        'BI_ANNUAL': 'Bi-Annual', 
        'QUARTERLY': 'Quarterly'
      };
      
      const periodName = periodMap[planningPeriod] || 'Plan';
      
      const generatedTitle = `${teamName} ${periodName} Procurement Plan ${fiscalYear}`;
      const generatedDescription = `${periodName.toLowerCase()} procurement plan for ${teamName} for the fiscal year ${fiscalYear}.`;
      
      // Only auto-fill if fields haven't been manually edited
      if (!titleManuallyEdited) {
        form.setFieldsValue({ title: generatedTitle });
      }
      if (!descriptionManuallyEdited) {
        form.setFieldsValue({ description: generatedDescription });
      }
    }
  }, [teamId, fiscalYear, planningPeriod, teams, titleManuallyEdited, descriptionManuallyEdited, form]);

  // Remove item from the procurement plan
  const removeItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  // Columns for the items table
  const itemColumns = [
    {
      title: 'Item Name',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Type',
      dataIndex: 'categoryType',
      key: 'categoryType',
      render: (type: string) => CATEGORY_TYPE_OPTIONS.find(o => o.value === type)?.label || type,
    },
    {
      title: 'Category',
      dataIndex: 'productCategory',
      key: 'productCategory',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (price: number) => `ETB ${price.toLocaleString()}`
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (price: number) => `ETB ${price.toLocaleString()}`
    },
    {
      title: 'Required Date',
      dataIndex: 'requiredDate',
      key: 'requiredDate',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : ''
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => <PriorityBadge priority={priority} />
    },
   {
      title: 'Actions',
      key: 'actions',
      render: (_: any, _record: IProcurementItem, index: number) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => editItem(index)}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(index)}
          />
        </Space>
      ),
    }
  ].filter(Boolean) as any[];

  // Add or update item in the procurement plan
  const saveItem = () => {
    itemForm.validateFields()
      .then(values => {
        const item: IProcurementItem = {
          id: editingIndex !== null ? items[editingIndex].id : `temp-${Date.now()}`,
          productName: values.itemName,
          productDescription: values.productDescription,
          productCategory: values.category,
          categoryType: values.categoryType || 'GOODS',
          quantity: values.quantity,
          unit: values.unit,
          unitPrice: values.unitPrice,
          totalPrice: values.quantity * values.unitPrice,
          requiredDate: values.requiredDate,
          quarter: 1, // This would be calculated based on the requiredDate
          period: '', // This would be calculated based on the requiredDate
          priority: values.priority,
          preferredSupplierId: values.preferredSupplierId,
          preferredSupplierName: values.preferredSupplierName,
          justification: values.justification,
          tags: []
        };
        
        if (editingIndex !== null) {
          // Update existing item
          const newItems = [...items];
          newItems[editingIndex] = item;
          setItems(newItems);
          setEditingIndex(null);
        } else {
          // Add new item
          setItems([...items, item]);
        }
        itemForm.resetFields();
        setIsItemModalVisible(false); // Close the modal after saving
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  // Edit item in the procurement plan
  const editItem = (index: number) => {
    const item = items[index];
    itemForm.setFieldsValue({
      itemName: item.productName,
      productDescription: item.productDescription,
      categoryType: item.categoryType || 'GOODS',
      category: item.productCategory,
      quantity: item.quantity,
      unit: item.unit,
      unitPrice: item.unitPrice,
      requiredDate: dayjs(item.requiredDate),
      priority: item.priority,
      preferredSupplierId: item.preferredSupplierId,
      preferredSupplierName: item.preferredSupplierName,
      justification: item.justification
    });
    setEditingIndex(index);
    setIsItemModalVisible(true); // Open the modal when editing
  };

  // Handle submit button success
  const handleSubmitSuccess = () => {
    // Call the parent's onSubmitForApproval if provided
    // Note: We don't have the response data here, so we pass undefined
    onSubmitForApproval?.(undefined);
  };

  // Get plan data for submission
  const getPlanDataForSubmission = () => {
    const formValues = form.getFieldsValue();
    return {
      ...formValues,
      items,
      templateId: form.getFieldValue('templateId'),
    };
  };

  // Handle saving draft (only validate main form fields)
  const handleSaveDraft = async () => {
    try {
      await form.validateFields();
      // Get form values and include items
      const formValues = form.getFieldsValue();
      onSaveDraft({
        ...formValues,
        items,
      });
    } catch (error) {
      console.log('Form validation failed:', error);
    }
  };

  return (
    <div>
      {formErrors.length > 0 && (
        <Alert
          message="Form Validation Errors"
          description={
            <ul style={{ margin: 0 }}>
              {formErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          planningPeriod: 'ANNUAL',
          fiscalYear: new Date().getFullYear(),
          ...initialValues,
        }}
      >
        <Card title="Plan Information" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="templateId"
                label="Plan Template"
              >
                <Select
                  placeholder="Select a template to pre-fill fields"
                  disabled={templatesLoading}
                  loading={templatesLoading}
                  allowClear
                  onChange={(value) => {
                    if (value) {
                      const selectedTemplate = templates.find((t: any) => t.id === value);
                      if (selectedTemplate) {
                        form.setFieldValue("teamId", selectedTemplate.teamId);
                        // Only set fiscal year if provided in template, otherwise keep default
                        if (selectedTemplate.fiscalYear) {
                          form.setFieldValue("fiscalYear", selectedTemplate.fiscalYear);
                        }
                        // Copy approval workflow from template
                        form.setFieldValue("steps", selectedTemplate.steps);
                        form.setFieldValue("approvalType", selectedTemplate.approvalType);
                        form.setFieldValue("externalWorkflow", selectedTemplate.externalWorkflow);
                        message.success(`Template '${selectedTemplate.name}' applied.`);
                      }
                    } else {
                      // Clear template-related fields if template is unselected
                      form.setFieldValue("teamId", undefined);
                      // Don't clear fiscal year - keep the default or user-entered value
                      form.setFieldValue("steps", undefined);
                      form.setFieldValue("approvalType", 'EXTERNAL');
                      form.setFieldValue("externalWorkflow", undefined);
                    }
                  }}
                >
                  {(templates as any[]).map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name} ({t.fiscalYear})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="teamId"
                label="Team"
                rules={[{ required: true, message: 'Please select a team' }]}
              >
                <Select
                  placeholder="Select team"
                  loading={teamsLoading}
                  disabled={teamsLoading || teamsError}
                >
                  {teamsLoading ? (
                    <Select.Option value="" disabled>
                      Loading teams...
                    </Select.Option>
                  ) : teamsError ? (
                    <Select.Option value="" disabled>
                      Error loading teams
                    </Select.Option>
                  ) : (
                    (teams as ITeam[]).map((team: ITeam) => (
                      <Select.Option key={team.id} value={team.id}>
                        {team.name}
                      </Select.Option>
                    ))
                  )}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="fiscalYear"
                label="Fiscal Year"
                rules={[{ required: true, message: 'Please enter fiscal year' }]}
              >
                <InputNumber
                  min={2020}
                  max={2030}
                  style={{ width: '100%' }}
                  placeholder="Enter fiscal year"
                />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="planningPeriod"
                label="Planning Period"
                rules={[{ required: true, message: 'Please select planning period' }]}
              >
                <Select placeholder="Select planning period">
                  <Select.Option value="ANNUAL">Annual</Select.Option>
                  <Select.Option value="BI_ANNUAL">Bi-Annual</Select.Option>
                  <Select.Option value="QUARTERLY">Quarterly</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Plan Title"
                rules={[{ required: true, message: 'Please enter a plan title' }]}
              >
                <Input 
                  placeholder="Enter plan title" 
                  onChange={() => setTitleManuallyEdited(true)}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="description"
                label="Description"
              >
                <Input.TextArea 
                  placeholder="Enter plan description" 
                  rows={1} 
                  onChange={() => setDescriptionManuallyEdited(true)}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row>
            <Form.Item name="steps" hidden/>
          </Row>
          <Row>
            <Form.Item name="externalWorkflow" hidden/>
          </Row>
          <Row>
            <Form.Item name="approvalType" hidden/>
          </Row>
        </Card>

        <Card
          title="Procurement Items"
          style={{ marginBottom: 24 }}
          extra={
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                itemForm.resetFields();
                itemForm.setFieldsValue({ categoryType: 'GOODS' });
                setEditingIndex(null);
                setIsItemModalVisible(true);
              }}
            />
          }
        >
          <Table
            dataSource={items}
            columns={itemColumns}
            rowKey={(_record, index) => index?.toString() || ''}
            pagination={false}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>ETB {items.reduce((sum, item) => sum + item.totalPrice, 0).toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={4}></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>

        <Card style={{ textAlign: "right" }}>
          <Space>
            <Button
              type="default"
              onClick={handleSaveDraft}
              icon={<SaveOutlined />}
              loading={saveButtonProps?.loading}
            >
              Save Draft
            </Button>
            <SubmitButton
              planId={isEdit ? planId : undefined}
              planData={!isEdit ? getPlanDataForSubmission() : undefined}
              buttonText="Save and Submit"
              onSuccess={handleSubmitSuccess}
              disabled={items.length === 0}
            />
            <Button icon={<CloseCircleOutlined />} onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Card>
      
      </Form>
      
      {/* Modal for adding/editing procurement items */}
      <Modal
        title={editingIndex !== null ? "Edit Procurement Item" : "Add Procurement Item"}
        visible={isItemModalVisible}
        onOk={saveItem}
        onCancel={() => {
          setIsItemModalVisible(false);
          itemForm.resetFields();
          setEditingIndex(null);
        }}
        okText={editingIndex !== null ? "Update Item" : "Add Item"}
        width={800}
      >
        <Form
          form={itemForm}
          layout="vertical"
          initialValues={{ categoryType: 'GOODS' }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="categoryType"
                label="Category Type"
                rules={[{ required: true, message: 'Please select a category type' }]}
              >
                <Radio.Group
                  onChange={() => itemForm.setFieldValue('category', undefined)}
                >
                  {CATEGORY_TYPE_OPTIONS.map(option => (
                    <Tooltip key={option.value} title={option.description}>
                      <span>
                        <Radio value={option.value} style={{ marginRight: 16 }}>
                          {option.label}
                        </Radio>
                      </span>
                    </Tooltip>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="itemName"
                label="Item Name"
                rules={[{ required: true, message: 'Please enter item name' }]}
              >
                <Input placeholder="Enter item name" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select or enter a category' }]}
              >
                <AutoComplete
                  placeholder="Select or type to search category"
                  options={(categoriesData[categoryType || 'GOODS'] || []).map(category => ({
                    value: category,
                    label: category,
                  }))}
                  filterOption={(inputValue, option) =>
                    option!.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="unit"
                label="Unit"
                rules={[{ required: true, message: 'Please select unit' }]}
                initialValue="pieces"
              >
                <Select placeholder="Select unit">
                  <Select.Option value="pieces">Pieces</Select.Option>
                  <Select.Option value="liter">Liter</Select.Option>
                  <Select.Option value="kg">Kilogram</Select.Option>
                  <Select.Option value="meter">Meter</Select.Option>
                  <Select.Option value="box">Box</Select.Option>
                  <Select.Option value="pack">Pack</Select.Option>
                  <Select.Option value="set">Set</Select.Option>
                  <Select.Option value="pair">Pair</Select.Option>
                  <Select.Option value="bottle">Bottle</Select.Option>
                  <Select.Option value="roll">Roll</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="unitPrice"
                label="Unit Price"
                rules={[{ required: true, message: 'Please enter unit price' }]}
              >
                <InputNumber
                  min={0}
                  formatter={value => `ETB ${value}`}
                  parser={value => value!.replace(/ETB\s?|(,*)/g, '') as any}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="requiredDate"
                label="Required Date"
                rules={[
                  ...(isRequirementDateRequired 
                    ? [{ required: true, message: 'Please select required date' }]
                    : []
                  )
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select placeholder="Select priority">
                  <Select.Option value="LOW">Low</Select.Option>
                  <Select.Option value="MEDIUM">Medium</Select.Option>
                  <Select.Option value="HIGH">High</Select.Option>
                  <Select.Option value="URGENT">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={24}>
              <Form.Item
                name="justification"
                label="Justification"
              >
                <Input.TextArea placeholder="Enter justification for this item" rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};